import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import api from "@/lib/api";
import { STEPS } from "@/components/onboard/ui";
import { StepVerifyCompany } from "@/components/onboard/StepVerifyCompany";
import { StepPhone } from "@/components/onboard/StepPhone";
import { StepCompany } from "@/components/onboard/StepCompany";
import { StepDocuments } from "@/components/onboard/StepDocuments";
import { StepSelfie } from "@/components/onboard/StepSelfie";
import { StepDrivers } from "@/components/onboard/StepDrivers";
import { StepPlan, StepAddons } from "@/components/onboard/StepPlan";
import { StepPayment } from "@/components/onboard/StepPayment";
import { StepAgreement } from "@/components/onboard/StepAgreement";
import { StepDone } from "@/components/onboard/StepDone";
import { Check, WarningCircle } from "@phosphor-icons/react";

const COMPONENTS = [StepVerifyCompany, StepPhone, StepCompany, StepDocuments, StepSelfie, StepDrivers, StepPlan, StepAddons, StepPayment, StepAgreement];

function Rail({ current, furthest, onJump }) {
  return (
    <ol className="space-y-1" data-testid="ob-rail">
      {STEPS.map((s, i) => {
        const done = i < furthest; const active = i === current; const reachable = i <= furthest && i >= 2;
        return (
          <li key={s}>
            <button disabled={!reachable} onClick={() => onJump(i)} data-testid={`ob-rail-${i}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? "bg-white/[0.07]" : reachable ? "hover:bg-white/[0.04]" : ""}`}>
              <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold shrink-0 ${done ? "bg-emerald-400 text-slate-900" : active ? "bg-sky-400 text-slate-900" : "bg-white/5 text-white/40 border border-white/10"}`}>{done ? <Check size={14} weight="bold" /> : i + 1}</span>
              <span className={`text-sm ${active ? "text-white font-semibold" : done ? "text-white/70" : "text-white/35"}`}>{s}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function CarrierOnboarding() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [current, setCurrent] = useState(0);
  const [dir, setDir] = useState(1);

  useEffect(() => {
    api.get(`/onboard/${token}`).then(({ data }) => { setState(data); setCurrent(data.submitted ? STEPS.length : Math.min(data.wizard.step || 0, STEPS.length)); }).catch(() => setInvalid(true));
  }, [token]);

  const go = (i) => { setDir(i > current ? 1 : -1); setCurrent(i); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const onSaved = (data) => { setState(data); go(current + 1); };
  const furthest = state ? (state.submitted ? STEPS.length : state.wizard.step || 0) : 0;
  const Step = COMPONENTS[current];
  const pct = Math.round((Math.min(current, STEPS.length) / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-[#05070d] text-white relative overflow-hidden" data-testid="carrier-onboarding-page">
      <div className="pointer-events-none absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-sky-500/20 blur-[140px]" />
      <div className="pointer-events-none absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-emerald-500/15 blur-[140px]" />
      <header className="relative z-10 h-16 flex items-center justify-between px-5 sm:px-10 border-b border-white/5 backdrop-blur-xl bg-black/20">
        <div className="flex items-center gap-2.5"><img src="/freightx-logo.webp" alt="Asterism Services" className="w-8 h-8 object-contain" /><span className="font-heading font-extrabold text-lg">Asterism Services</span></div>
        {state && !state.submitted && <span className="text-xs text-white/50 font-mono" data-testid="ob-progress">{pct}% Complete</span>}
      </header>
      <div className="relative z-10 h-1 bg-white/5"><div className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-[width] duration-500" style={{ width: `${state?.submitted ? 100 : pct}%` }} /></div>

      {invalid ? (
        <div className="relative z-10 max-w-md mx-auto text-center py-32"><WarningCircle size={48} className="text-amber-400 mx-auto" /><h1 className="font-heading font-extrabold text-2xl mt-4">This Link Is Invalid</h1><p className="text-white/55 mt-2">Please contact your Asterism representative for a new onboarding link.</p></div>
      ) : !state ? (
        <p className="relative z-10 text-center py-32 text-white/40">Loading your onboarding…</p>
      ) : (
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-10 py-10 lg:py-14 grid lg:grid-cols-[260px_1fr] gap-10">
          <aside className="hidden lg:block sticky top-10 self-start">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/40 mb-4 px-3">Carrier Onboarding</p>
            <Rail current={current} furthest={furthest} onJump={go} />
          </aside>
          <main className="min-w-0 rounded-[2rem] border border-white/10 bg-white/[0.025] backdrop-blur-2xl p-6 sm:p-10 shadow-[0_40px_120px_-40px_rgba(0,0,0,.8)]">
            <p className="lg:hidden text-xs text-white/40 mb-4 font-mono">Step {Math.min(current + 1, STEPS.length)} of {STEPS.length}</p>
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div key={current} custom={dir} initial={{ opacity: 0, x: dir * 60 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -60 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
                {current >= STEPS.length || state.submitted
                  ? <StepDone token={token} state={state} />
                  : <Step token={token} state={state} onSaved={onSaved} onBack={current > 0 && current !== 2 ? () => go(current - 1) : current === 2 ? null : null} />}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      )}
    </div>
  );
}
